/**
 * Bucketed histograms for hot-path latencies and operation costs.
 *
 * **PRODUCTION SAFETY — identical pattern to `perf-counters.ts`.**
 *
 * Every `_observe(...)` call site is wrapped in
 * `if (process.env.NODE_ENV !== 'production')` so bundlers fold the
 * surrounding branch out at build time. The `_observe` function body
 * also no-ops in production as belt-and-braces. The histogram module
 * lives in `/devtool` subpath territory and is never imported by the
 * main barrel — production consumers tree-shake it entirely.
 *
 * Why bucketed (not raw samples): a 500-event ring buffer doesn't tell
 * you "how often was dispatch > 10 ms over the last 10 minutes" — only
 * what's in the buffer right now. Bucketed histograms accumulate
 * forever (until reset), surface p50/p95/p99 on demand, and cost one
 * `Array.prototype.binarySearch + ++` per observation. Trade-off: we
 * lose individual sample identity, gain unbounded retention with
 * constant memory.
 *
 * Bucket layout — log-spaced for both latency (ms) and scan steps:
 *   latencyBounds:    0.1, 0.5, 1, 2, 5, 10, 25, 50, 100, 250, 500, 1000
 *   scanStepsBounds:  1, 2, 5, 10, 25, 50, 100, 250, 500
 *
 * Buckets are LE (less-or-equal) — bucket[i] counts observations where
 * `value <= bounds[i]`. The last bucket (overflow) counts everything
 * above the largest boundary.
 *
 * Percentile estimation is linear-interp inside the matched bucket
 * using the running count + sum. Accurate to roughly ±bucket-width;
 * for tuning work this is sufficient — and far cheaper than a real
 * HDR histogram or t-digest.
 */

const PIN_KEY = '__morfi_perf_histograms__' as const;
const PERF_HISTOGRAMS_SCHEMA_VERSION = 3;

/** Latency-style bounds (ms). */
export const LATENCY_BOUNDS: readonly number[] = [
  0.1, 0.5, 1, 2, 5, 10, 25, 50, 100, 250, 500, 1000,
];
/** Scan-step bounds (integer counts). */
export const SCAN_STEPS_BOUNDS: readonly number[] = [1, 2, 5, 10, 25, 50, 100, 250, 500];
/** Protobuf payload-size bounds (bytes). */
export const PAYLOAD_BYTES_BOUNDS: readonly number[] = [
  64, 128, 256, 512, 1_024, 2_048, 4_096, 8_192, 16_384, 32_768, 65_536, 131_072,
];
/** Buffered-event batch-size bounds (events). */
export const BATCH_COUNT_BOUNDS: readonly number[] = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1_000];

export interface HistogramBucket {
  /** Upper bound of this bucket (inclusive). `Infinity` for the overflow bucket. */
  readonly le: number;
  /** Count of observations falling in this bucket. */
  readonly count: number;
}

export interface Histogram {
  /** Total observations. */
  readonly count: number;
  /** Sum of observed values (for mean). */
  readonly sum: number;
  readonly min: number;
  readonly max: number;
  readonly mean: number;
  readonly p50: number;
  readonly p95: number;
  readonly p99: number;
  readonly buckets: readonly HistogramBucket[];
}

export interface PerfHistograms {
  /** Wall-clock ms of the most-recent reset. */
  readonly resetAt: number;
  /** Wall-clock ms when this snapshot was projected. */
  readonly observedAt: number;
  /** `_runDispatchBranch` total duration per NATS event (ms). */
  readonly dispatchDurationMs: Histogram;
  /** Row scan iteration count per matched applyEvent dispatch. */
  readonly applyEventRowScanSteps: Histogram;
  /** `createFieldHook.select` callback duration (ms). */
  readonly fieldHookSelectMs: Histogram;
  /** Mutation total wall-clock duration onMutate → settled (ms). */
  readonly mutationDurationMs: Histogram;
  /** Authoritative position-list refetch duration (ms). */
  readonly positionReconcileDurationMs: Histogram;
  /** Raw protobuf payload bytes at the wire receive boundary. */
  readonly payloadBytes: Histogram;
  /** Synchronous wire callback dispatch duration (ms). */
  readonly wireDispatchDurationMs: Histogram;
  /** Per-handler protobuf decode duration (ms). */
  readonly decodeDurationMs: Histogram;
  /** Coalesced cache apply duration for one buffered batch (ms). */
  readonly applyFlushDurationMs: Histogram;
  /** Age of the oldest event when a coalesced batch begins applying (ms). */
  readonly applyOldestEventAgeMs: Histogram;
  /** Events folded into one coalesced cache write. */
  readonly applyBatchEvents: Histogram;
  /** Non-empty wire arrival buckets over 100 ms windows. */
  readonly wireArrivalsPer100ms: Histogram;
  /** Non-empty wire arrival buckets over one-second windows. */
  readonly wireArrivalsPer1s: Histogram;
}

interface InternalHistogram {
  readonly bounds: readonly number[];
  buckets: number[];
  count: number;
  sum: number;
  min: number;
  max: number;
}

interface InternalStore {
  readonly schemaVersion: number;
  resetAt: number;
  dispatchDurationMs: InternalHistogram;
  applyEventRowScanSteps: InternalHistogram;
  fieldHookSelectMs: InternalHistogram;
  mutationDurationMs: InternalHistogram;
  positionReconcileDurationMs: InternalHistogram;
  payloadBytes: InternalHistogram;
  wireDispatchDurationMs: InternalHistogram;
  decodeDurationMs: InternalHistogram;
  applyFlushDurationMs: InternalHistogram;
  applyOldestEventAgeMs: InternalHistogram;
  applyBatchEvents: InternalHistogram;
  wireArrivalsPer100ms: InternalHistogram;
  wireArrivalsPer1s: InternalHistogram;
  wireWindow100ms: { bucket: number; count: number };
  wireWindow1s: { bucket: number; count: number };
}

function makeHist(bounds: readonly number[]): InternalHistogram {
  return {
    bounds,
    // +1 slot for overflow ( > largest bound )
    buckets: new Array(bounds.length + 1).fill(0),
    count: 0,
    sum: 0,
    min: Number.POSITIVE_INFINITY,
    max: 0,
  };
}

function makeStore(): InternalStore {
  return {
    schemaVersion: PERF_HISTOGRAMS_SCHEMA_VERSION,
    resetAt: Date.now(),
    dispatchDurationMs: makeHist(LATENCY_BOUNDS),
    applyEventRowScanSteps: makeHist(SCAN_STEPS_BOUNDS),
    fieldHookSelectMs: makeHist(LATENCY_BOUNDS),
    mutationDurationMs: makeHist(LATENCY_BOUNDS),
    positionReconcileDurationMs: makeHist(LATENCY_BOUNDS),
    payloadBytes: makeHist(PAYLOAD_BYTES_BOUNDS),
    wireDispatchDurationMs: makeHist(LATENCY_BOUNDS),
    decodeDurationMs: makeHist(LATENCY_BOUNDS),
    applyFlushDurationMs: makeHist(LATENCY_BOUNDS),
    applyOldestEventAgeMs: makeHist(LATENCY_BOUNDS),
    applyBatchEvents: makeHist(BATCH_COUNT_BOUNDS),
    wireArrivalsPer100ms: makeHist(BATCH_COUNT_BOUNDS),
    wireArrivalsPer1s: makeHist(BATCH_COUNT_BOUNDS),
    wireWindow100ms: { bucket: -1, count: 0 },
    wireWindow1s: { bucket: -1, count: 0 },
  };
}

declare global {
  // eslint-disable-next-line no-var
  var __morfi_perf_histograms__: InternalStore | undefined;
}

function store(): InternalStore {
  const g = globalThis as { [PIN_KEY]?: InternalStore };
  if (g[PIN_KEY]?.schemaVersion !== PERF_HISTOGRAMS_SCHEMA_VERSION) g[PIN_KEY] = makeStore();
  return g[PIN_KEY];
}

type HistogramKey =
  | 'dispatchDurationMs'
  | 'applyEventRowScanSteps'
  | 'fieldHookSelectMs'
  | 'mutationDurationMs'
  | 'positionReconcileDurationMs'
  | 'payloadBytes'
  | 'wireDispatchDurationMs'
  | 'decodeDurationMs'
  | 'applyFlushDurationMs'
  | 'applyOldestEventAgeMs'
  | 'applyBatchEvents'
  | 'wireArrivalsPer100ms'
  | 'wireArrivalsPer1s';

/**
 * Record one observation. MUST be invoked only from
 * `process.env.NODE_ENV !== 'production'` branches at the call site —
 * the in-body NODE_ENV check is belt-and-braces.
 *
 * Cost: linear scan over `bounds.length` (<= 12 elements) + 4 integer
 * writes. Well under a microsecond per call.
 */
export function _observe(key: HistogramKey, value: number): void {
  if (process.env.NODE_ENV === 'production') return;
  if (!Number.isFinite(value) || value < 0) return;
  recordObservation(store()[key], value);
}

function recordObservation(h: InternalHistogram, value: number): void {
  const bounds = h.bounds;
  let idx = bounds.length; // default overflow
  for (let i = 0; i < bounds.length; i += 1) {
    const b = bounds[i];
    if (b !== undefined && value <= b) {
      idx = i;
      break;
    }
  }
  h.buckets[idx] = (h.buckets[idx] ?? 0) + 1;
  h.count += 1;
  h.sum += value;
  if (value < h.min) h.min = value;
  if (value > h.max) h.max = value;
}

/** @internal Record one payload and update non-empty 100 ms / 1 s arrival windows. */
export function _observeWireArrival(bytes: number, at: number = Date.now()): void {
  if (process.env.NODE_ENV === 'production') return;
  _observe('payloadBytes', bytes);
  const s = store();
  observeArrivalWindow(s.wireWindow100ms, Math.floor(at / 100), 'wireArrivalsPer100ms');
  observeArrivalWindow(s.wireWindow1s, Math.floor(at / 1_000), 'wireArrivalsPer1s');
}

function observeArrivalWindow(
  window: { bucket: number; count: number },
  bucket: number,
  histogram: 'wireArrivalsPer100ms' | 'wireArrivalsPer1s',
): void {
  if (window.bucket === bucket) {
    window.count += 1;
    return;
  }
  if (window.count > 0) _observe(histogram, window.count);
  window.bucket = bucket;
  window.count = 1;
}

function percentile(h: InternalHistogram, p: number): number {
  if (h.count === 0) return 0;
  const target = h.count * p;
  let cumulative = 0;
  for (let i = 0; i < h.buckets.length; i += 1) {
    const bucket = h.buckets[i] ?? 0;
    cumulative += bucket;
    if (cumulative >= target) {
      // Bucket i contains the p% mark. Linear interpolation inside
      // the bucket using lower-bound (previous bound) and upper-
      // bound (this bound). The overflow bucket has no upper, so we
      // return min(max-observed, lower * 1.5) as a fudged ceiling.
      const upper = h.bounds[i] ?? h.max;
      const lower = i === 0 ? 0 : (h.bounds[i - 1] ?? 0);
      if (bucket === 0) return lower;
      const positionInBucket = (cumulative - target) / bucket;
      return upper - positionInBucket * (upper - lower);
    }
  }
  return h.max;
}

function projectHistogram(h: InternalHistogram): Histogram {
  const buckets: HistogramBucket[] = [];
  for (let i = 0; i < h.bounds.length; i += 1) {
    const le = h.bounds[i];
    if (le !== undefined) {
      buckets.push({ le, count: h.buckets[i] ?? 0 });
    }
  }
  buckets.push({
    le: Number.POSITIVE_INFINITY,
    count: h.buckets[h.bounds.length] ?? 0,
  });
  return {
    count: h.count,
    sum: h.sum,
    min: h.count === 0 ? 0 : h.min,
    max: h.max,
    mean: h.count === 0 ? 0 : h.sum / h.count,
    p50: percentile(h, 0.5),
    p95: percentile(h, 0.95),
    p99: percentile(h, 0.99),
    buckets,
  };
}

function projectHistogramWithCurrent(h: InternalHistogram, current: number): Histogram {
  if (current <= 0) return projectHistogram(h);
  const snapshot: InternalHistogram = { ...h, buckets: [...h.buckets] };
  recordObservation(snapshot, current);
  return projectHistogram(snapshot);
}

export function getPerfHistograms(): PerfHistograms {
  const s = store();
  return {
    resetAt: s.resetAt,
    observedAt: Date.now(),
    dispatchDurationMs: projectHistogram(s.dispatchDurationMs),
    applyEventRowScanSteps: projectHistogram(s.applyEventRowScanSteps),
    fieldHookSelectMs: projectHistogram(s.fieldHookSelectMs),
    mutationDurationMs: projectHistogram(s.mutationDurationMs),
    positionReconcileDurationMs: projectHistogram(s.positionReconcileDurationMs),
    payloadBytes: projectHistogram(s.payloadBytes),
    wireDispatchDurationMs: projectHistogram(s.wireDispatchDurationMs),
    decodeDurationMs: projectHistogram(s.decodeDurationMs),
    applyFlushDurationMs: projectHistogram(s.applyFlushDurationMs),
    applyOldestEventAgeMs: projectHistogram(s.applyOldestEventAgeMs),
    applyBatchEvents: projectHistogram(s.applyBatchEvents),
    wireArrivalsPer100ms: projectHistogramWithCurrent(
      s.wireArrivalsPer100ms,
      s.wireWindow100ms.count,
    ),
    wireArrivalsPer1s: projectHistogramWithCurrent(s.wireArrivalsPer1s, s.wireWindow1s.count),
  };
}

export function resetPerfHistograms(): void {
  const g = globalThis as { [PIN_KEY]?: InternalStore };
  g[PIN_KEY] = makeStore();
}
